/**
 * Generic image lightbox.
 *
 * Ports static/src/pages/Projects.jsx:304-369. Triggers are any element with
 * [data-awdd-lightbox="<group>"], carrying data-awdd-lightbox-image and,
 * optionally, -eyebrow and -title. Opening one trigger makes its whole group
 * pageable without reopening.
 *
 * GOLD, NOT ACCENT. The reference's `accent` IS gold (#F4CD07); this theme's
 * accent is brand blue. The panel sits on bg-ink-deep/90, where copying the
 * reference's hover:*-accent verbatim would render brand blue at 2.88:1
 * instead of gold's 11.72:1. Same pixels as the reference, different token.
 * The overlay ground is fixed, so gold is unbranched here.
 *
 * Scroll locking goes through scroll-lock.js rather than writing
 * document.body.style.overflow directly: the reference stores prevOverflow and
 * restores it, and that module is the theme's shared implementation of exactly
 * that, with the nesting the three overlays need.
 *
 * The dialog is built once, lazily, and reused. Every listener added on open
 * is removed on close, so paging never accumulates keydown handlers.
 */

import { setScrollLock } from './scroll-lock.js';
import { icon } from './icons.js';

const LOCK_OWNER = 'lightbox';

let overlay = null;
let figure = null;
let imageEl = null;
let eyebrowEl = null;
let titleEl = null;
let prevBtn = null;
let nextBtn = null;
let navWrap = null;

let items = [];
let index = 0;
let lastFocused = null;

function build() {
	if (overlay) return;

	overlay = document.createElement('div');
	overlay.className =
		'fixed inset-0 z-[100] flex items-center justify-center bg-ink-deep/90 p-4 backdrop-blur-sm sm:p-8';
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');
	overlay.hidden = true;
	overlay.innerHTML = `
		<button type="button" data-lb-close aria-label="Close"
			class="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:border-gold hover:text-gold">
			${icon('close', 'h-[18px] w-[18px]')}
		</button>
		<div data-lb-nav>
			<button type="button" data-lb-prev aria-label="Previous image"
				class="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:border-gold hover:text-gold sm:left-6">
				${icon('arrow', 'h-5 w-5 rotate-180')}
			</button>
			<button type="button" data-lb-next aria-label="Next image"
				class="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:border-gold hover:text-gold sm:right-6">
				${icon('arrow', 'h-5 w-5')}
			</button>
		</div>
		<figure data-lb-figure class="flex max-h-full max-w-4xl flex-col">
			<img data-lb-img alt="" class="max-h-[80vh] w-auto rounded-xl object-contain" />
			<figcaption class="mt-3 text-center">
				<div data-lb-eyebrow class="font-sans text-[9px] uppercase tracking-wide text-gold"></div>
				<div data-lb-title class="mt-1 font-display text-[15px] font-bold text-white"></div>
			</figcaption>
		</figure>
	`;

	figure = overlay.querySelector('[data-lb-figure]');
	imageEl = overlay.querySelector('[data-lb-img]');
	eyebrowEl = overlay.querySelector('[data-lb-eyebrow]');
	titleEl = overlay.querySelector('[data-lb-title]');
	navWrap = overlay.querySelector('[data-lb-nav]');
	prevBtn = overlay.querySelector('[data-lb-prev]');
	nextBtn = overlay.querySelector('[data-lb-next]');

	// Backdrop closes; the figure does not.
	overlay.addEventListener('click', close);
	figure.addEventListener('click', (e) => e.stopPropagation());
	overlay.querySelector('[data-lb-close]').addEventListener('click', (e) => {
		e.stopPropagation();
		close();
	});
	prevBtn.addEventListener('click', (e) => {
		e.stopPropagation();
		step(-1);
	});
	nextBtn.addEventListener('click', (e) => {
		e.stopPropagation();
		step(1);
	});

	document.body.appendChild(overlay);
}

function render() {
	const item = items[index];
	if (!item) return;

	imageEl.src = item.image;
	imageEl.alt = item.title || '';

	// The reference falls back to the Sintalock eyebrow when an item has none.
	eyebrowEl.textContent = item.eyebrow || 'Sintalock®';
	titleEl.textContent = item.title || '';
	titleEl.hidden = !item.title;

	// Prev/next exist only for a group with more than one image.
	navWrap.hidden = items.length < 2;

	overlay.setAttribute('aria-label', item.title || 'Image viewer');
}

function step(dir) {
	if (items.length < 2) return;
	// Wraps at both ends, matching the reference.
	index = (index + dir + items.length) % items.length;
	render();
}

function onKey(e) {
	if (e.key === 'Escape') {
		close();
	} else if (e.key === 'ArrowLeft') {
		step(-1);
	} else if (e.key === 'ArrowRight') {
		step(1);
	}
}

function close() {
	if (!overlay || overlay.hidden) return;
	document.removeEventListener('keydown', onKey);
	overlay.hidden = true;
	setScrollLock(LOCK_OWNER, false);
	if (lastFocused) {
		lastFocused.focus();
		lastFocused = null;
	}
}

function open(group, start, trigger) {
	build();
	items = group;
	index = start;
	lastFocused = trigger || null;

	overlay.hidden = false;
	setScrollLock(LOCK_OWNER, true);
	document.addEventListener('keydown', onKey);
	render();
	overlay.querySelector('[data-lb-close]').focus();
}

export default function initLightbox() {
	const triggers = Array.from(document.querySelectorAll('[data-awdd-lightbox]'));
	if (!triggers.length) return;

	// Group the triggers so paging covers the whole group, not just one image.
	const groups = new Map();
	triggers.forEach((trigger) => {
		const name = trigger.getAttribute('data-awdd-lightbox');
		if (!groups.has(name)) groups.set(name, []);
		groups.get(name).push(trigger);
	});

	groups.forEach((groupTriggers) => {
		const group = groupTriggers.map((t) => ({
			image: t.getAttribute('data-awdd-lightbox-image') || '',
			eyebrow: t.getAttribute('data-awdd-lightbox-eyebrow') || '',
			title: t.getAttribute('data-awdd-lightbox-title') || '',
		}));

		groupTriggers.forEach((trigger, i) => {
			trigger.addEventListener('click', (e) => {
				e.preventDefault();
				open(group, i, trigger);
			});
		});
	});
}
